import React, { useState } from 'react';
import { IStateRequirement, ICategory } from '../api/StateRequirements';

interface StateRequirementEditProps {
  stateRequirement: IStateRequirement;
  onSave: (updated: IStateRequirement) => void;
  onCancel: () => void;
}

const StateRequirementEdit: React.FC<StateRequirementEditProps> = ({ stateRequirement, onSave, onCancel }) => {
  const [form, setForm] = useState<IStateRequirement>({ ...stateRequirement });
  const [error, setError] = useState<string>('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setForm(f => ({ ...f, [name]: type === 'number' ? (value === '' ? undefined : Number(value)) : value }));
  };

  const validate = () => {
    if (!form.professionID || form.professionID.trim() === '') return 'Profession ID is required.';
    if (!form.stateID || isNaN(Number(form.stateID))) return 'State ID is required and must be a number.';
    if (!form.effectiveDate) return 'Effective date is required.';
    if (!form.totalCreditsRequired || isNaN(Number(form.totalCreditsRequired))) return 'Total credits required is required and must be a number.';
    if (!form.cycleLengthYears || isNaN(Number(form.cycleLengthYears))) return 'Cycle length (years) is required and must be a number.';
    return '';
  };

  const handleSave = () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError('');
    onSave(form);
  };

  return (
    <div className="modal d-block" tabIndex={-1} style={{ background: 'rgba(0,0,0,0.3)' }}>
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Edit State Requirement</h5>
            <button type="button" className="btn-close" onClick={onCancel}></button>
          </div>
          <div className="modal-body">
            {error && <div className="alert alert-danger">{error}</div>}
            <div className="mb-2">
              <label className="form-label">Profession ID *</label>
              <input className="form-control" name="professionID" value={form.professionID} onChange={handleChange} required />
            </div>
            <div className="mb-2">
              <label className="form-label">State ID *</label>
              <input className="form-control" name="stateID" type="number" value={form.stateID} onChange={handleChange} required />
            </div>
            <div className="mb-2">
              <label className="form-label">Effective Date *</label>
              <input className="form-control" name="effectiveDate" type="date" value={form.effectiveDate ? String(form.effectiveDate).slice(0,10) : ''} onChange={handleChange} required />
            </div>
            <div className="mb-2">
              <label className="form-label">Expiry Date</label>
              <input className="form-control" name="expiryDate" type="date" value={form.expiryDate ? String(form.expiryDate).slice(0,10) : ''} onChange={handleChange} />
            </div>
            <div className="mb-2">
              <label className="form-label">Total Credits Required *</label>
              <input className="form-control" name="totalCreditsRequired" type="number" value={form.totalCreditsRequired} onChange={handleChange} required />
            </div>
            <div className="mb-2">
              <label className="form-label">Cycle Length (years) *</label>
              <input className="form-control" name="cycleLengthYears" type="number" value={form.cycleLengthYears} onChange={handleChange} required />
            </div>
            <div className="mb-2">
              <label className="form-label">Allow Carryover</label>
              <select className="form-select" name="allowCarryover" value={form.allowCarryover ? 'true' : 'false'} onChange={e => setForm(f => ({ ...f, allowCarryover: e.target.value === 'true' }))}>
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
            </div>
            <div className="mb-2">
              <label className="form-label">Carryover Max Credits</label>
              <input className="form-control" name="carryoverMaxCredits" type="number" value={form.carryoverMaxCredits ?? ''} onChange={handleChange} />
            </div>
            <div className="mb-2">
              <label className="form-label">Self Study Max Percent</label>
              <input className="form-control" name="selfStudyMaxPercent" type="number" value={form.selfStudyMaxPercent ?? ''} onChange={handleChange} />
            </div>
            <div className="mb-2">
              <label className="form-label">Lecture Min Percent</label>
              <input className="form-control" name="lectureMinPercent" type="number" value={form.lectureMinPercent ?? ''} onChange={handleChange} />
            </div>
            
            {/* Fixed Cycle Dates Section */}
            <hr className="my-3" />
            <h6 className="fw-bold">Fixed Cycle Dates</h6>
            <div className="mb-2">
              <label className="form-label">Has Fixed Cycle Dates</label>
              <select 
                className="form-select" 
                name="hasFixedCycleDates" 
                value={form.hasFixedCycleDates ? 'true' : 'false'} 
                onChange={e => setForm(f => ({ ...f, hasFixedCycleDates: e.target.value === 'true' }))}
              >
                <option value="false">No (User-defined dates)</option>
                <option value="true">Yes (Fixed dates)</option>
              </select>
            </div>
            
            {form.hasFixedCycleDates && (
              <>
                <div className="row">
                  <div className="col-6 mb-2">
                    <label className="form-label">Start Month</label>
                    <select 
                      className="form-select" 
                      name="fixedCycleStartMonth" 
                      value={form.fixedCycleStartMonth ?? ''} 
                      onChange={e => setForm(f => ({ ...f, fixedCycleStartMonth: e.target.value ? parseInt(e.target.value) : undefined }))}
                    >
                      <option value="">Select...</option>
                      <option value="1">January</option>
                      <option value="2">February</option>
                      <option value="3">March</option>
                      <option value="4">April</option>
                      <option value="5">May</option>
                      <option value="6">June</option>
                      <option value="7">July</option>
                      <option value="8">August</option>
                      <option value="9">September</option>
                      <option value="10">October</option>
                      <option value="11">November</option>
                      <option value="12">December</option>
                    </select>
                  </div>
                  <div className="col-6 mb-2">
                    <label className="form-label">Start Day</label>
                    <input 
                      className="form-control" 
                      name="fixedCycleStartDay" 
                      type="number" 
                      min="1" 
                      max="31" 
                      value={form.fixedCycleStartDay ?? ''} 
                      onChange={e => setForm(f => ({ ...f, fixedCycleStartDay: e.target.value ? parseInt(e.target.value) : undefined }))}
                      placeholder="1-31"
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-6 mb-2">
                    <label className="form-label">End Month</label>
                    <select 
                      className="form-select" 
                      name="fixedCycleEndMonth" 
                      value={form.fixedCycleEndMonth ?? ''} 
                      onChange={e => setForm(f => ({ ...f, fixedCycleEndMonth: e.target.value ? parseInt(e.target.value) : undefined }))}
                    >
                      <option value="">Select...</option>
                      <option value="1">January</option>
                      <option value="2">February</option>
                      <option value="3">March</option>
                      <option value="4">April</option>
                      <option value="5">May</option>
                      <option value="6">June</option>
                      <option value="7">July</option>
                      <option value="8">August</option>
                      <option value="9">September</option>
                      <option value="10">October</option>
                      <option value="11">November</option>
                      <option value="12">December</option>
                    </select>
                  </div>
                  <div className="col-6 mb-2">
                    <label className="form-label">End Day</label>
                    <input 
                      className="form-control" 
                      name="fixedCycleEndDay" 
                      type="number" 
                      min="1" 
                      max="31" 
                      value={form.fixedCycleEndDay ?? ''} 
                      onChange={e => setForm(f => ({ ...f, fixedCycleEndDay: e.target.value ? parseInt(e.target.value) : undefined }))}
                      placeholder="1-31"
                    />
                  </div>
                </div>
                <div className="mb-2">
                  <label className="form-label">Cycle Date Notes</label>
                  <input 
                    className="form-control" 
                    name="cycleDateNotes" 
                    value={form.cycleDateNotes || ''} 
                    onChange={handleChange}
                    placeholder="e.g., Texas architects must renew annually on the calendar year"
                  />
                  <small className="text-muted">Explain the fixed cycle policy to users</small>
                </div>
                {form.fixedCycleStartMonth && form.fixedCycleStartDay && form.fixedCycleEndMonth && form.fixedCycleEndDay && (
                  <div className="alert alert-info mb-2">
                    <strong>Preview:</strong> Cycles will run from {
                      new Date(2024, form.fixedCycleStartMonth - 1, form.fixedCycleStartDay).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
                    } to {
                      new Date(2024, form.fixedCycleEndMonth - 1, form.fixedCycleEndDay).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
                    } each year
                  </div>
                )}
              </>
            )}
            <hr className="my-3" />
            
            <div className="mb-2">
              <label className="form-label">Notes</label>
              <input className="form-control" name="notes" value={form.notes || ''} onChange={handleChange} />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
            <button type="button" className="btn btn-primary" onClick={handleSave}>Save</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StateRequirementEdit;
